import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

export const CONSENT_STORAGE_KEY = "britz_cookie_consent";

export type ConsentSettings = {
  necessary: true;
  statistics: boolean;
  externalMedia: boolean;
};

type StoredConsent = ConsentSettings & {
  version: 1;
  updatedAt: string;
};

type ConsentContextValue = {
  consent: ConsentSettings | null;
  settingsOpen: boolean;
  hasChoice: boolean;
  acceptAll: () => void;
  acceptNecessary: () => void;
  saveConsent: (settings: Omit<ConsentSettings, "necessary">) => void;
  enableExternalMedia: () => void;
  openSettings: () => void;
  closeSettings: () => void;
};

const defaultConsent: ConsentSettings = {
  necessary: true,
  statistics: false,
  externalMedia: false,
};

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

function readStoredConsent(): ConsentSettings | null {
  if (typeof window === "undefined") return null;

  const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  if (!raw) return null;

  if (raw === "accepted") {
    return { necessary: true, statistics: true, externalMedia: true };
  }

  if (raw === "necessary") {
    return defaultConsent;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    return {
      necessary: true,
      statistics: parsed.statistics === true,
      externalMedia: parsed.externalMedia === true,
    };
  } catch {
    return null;
  }
}

function storeConsent(settings: ConsentSettings) {
  window.localStorage.setItem(
    CONSENT_STORAGE_KEY,
    JSON.stringify({
      ...settings,
      version: 1,
      updatedAt: new Date().toISOString(),
    } satisfies StoredConsent),
  );
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentSettings | null>(() => readStoredConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  const save = (settings: ConsentSettings) => {
    storeConsent(settings);
    setConsent(settings);
    setSettingsOpen(false);
  };

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      settingsOpen,
      hasChoice: consent !== null,
      acceptAll: () => save({ necessary: true, statistics: true, externalMedia: true }),
      acceptNecessary: () => save(defaultConsent),
      saveConsent: (settings) => save({ necessary: true, ...settings }),
      enableExternalMedia: () =>
        save({
          necessary: true,
          statistics: consent?.statistics === true,
          externalMedia: true,
        }),
      openSettings: () => setSettingsOpen(true),
      closeSettings: () => setSettingsOpen(false),
    }),
    [consent, settingsOpen],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within ConsentProvider");
  }
  return context;
}
